import { EntityModel } from '@/interfaces'
import {
  ALL_TRAVELS_QUERY,
  CREATE_TRAVEL_MUTATION,
  DELETE_TRAVEL_MUTATION,
  FORCE_DELETE_TRAVEL_MUTATION,
  SINGLE_TRAVEL_QUERY,
  UPDATE_TRAVEL_MUTATION,
} from './graphql'
import travelAvailabilityStatusEntity from '@/entities/travelAvailabilityStatus/entity'
import employeeEntity from '@/entities/employee/entity'
import travelLabelEntity from '@/entities/travelLabel/entity'
import elementSeoSettingsEntity from '@/entities/elementSeoSettings/entity'
import travelObjectTenantSettingsEntity from '@/entities/travelObjectTenantSettings/entity'
import travelHighlightEntity from '@/entities/travelHighlight/entity'
import travelPartnerEntity from '@/entities/travelPartner/entity'
import travelGuideEntity from '@/entities/travelGuide/entity'
import travelTeamEntity from '@/entities/travelTeam/entity'
import travelAccommodationAssignmentEntity from '@/entities/travelAccommodationAssignment/entity'
import accommodationEntity from '@/entities/accommodation/entity'
import travelDestinationEntity from '@/entities/travelDestination/entity'
import travelTestimonialEntity from '@/entities/travelTestimonial/entity'
import mediaFileEntity from '@/entities/mediaFile/entity'
import imageGalleryEntity from '@/entities/imageGallery/entity'
import travelExtensionEntity from '@/entities/travelExtension/entity'
import itineraryEntity from '@/entities/itinerary/entity'
import travelPricingEntity from '@/entities/travelPricing/entity'
import travelFaqEntity from '@/entities/travelFaq/entity'

const travelModel: EntityModel = {
  name: 'travel',
  namePlural: 'travels',
  uniqueName: 'travel',
  uniqueNamePlural: 'travels',
  path: 'travel/travel',
  baseUrl: '/travels',
  translationPrefix: 'entities:travel.',
  getDisplayName: (values: any) => {
    return values.name
  },
  gql: {
    getAll: ALL_TRAVELS_QUERY,
    getSingle: SINGLE_TRAVEL_QUERY,
    create: CREATE_TRAVEL_MUTATION,
    update: UPDATE_TRAVEL_MUTATION,
    delete: DELETE_TRAVEL_MUTATION,
    forceDelete: FORCE_DELETE_TRAVEL_MUTATION,
  },
  attributes: {
    name: {
      name: 'name',
      label: 'entities:travel.attributes.name.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    isBestseller: {
      name: 'isBestseller',
      label: 'entities:travel.attributes.isBestseller.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: true,
      inputType: 'switch',
      validationType: 'string',
      validations: [],
    },
    startDate: {
      name: 'startDate',
      label: 'entities:travel.attributes.startDate.label',
      type: 'value',
      dataType: 'Date',
      defaultInput: false,
      inputType: 'date',
      isStartDate: true,
      endDateAttribute: 'endDate',
      validationType: 'string',
      validations: [],
    },
    endDate: {
      name: 'endDate',
      label: 'entities:travel.attributes.endDate.label',
      type: 'value',
      dataType: 'Date',
      defaultInput: false,
      inputType: 'date',
      isEndDate: true,
      startDateAttribute: 'startDate',
      validationType: 'string',
      validations: [],
    },
    availabilityStatus: {
      name: 'availabilityStatus',
      label: 'entities:travel.attributes.availabilityStatus.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return travelAvailabilityStatusEntity
      },
      defaultInput: true,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    executionStatus: {
      name: 'executionStatus',
      label: 'entities:travel.attributes.executionStatus.label',
      type: 'value',
      dataType: 'string',
      defaultValue: 'open',
      defaultInput: false,
      inputType: 'select',
      selectOptions: [
        {
          label: 'entities:travel.attributes.executionStatus.selectOptions.open.label',
          value: 'open',
        },
        {
          label: 'entities:travel.attributes.executionStatus.selectOptions.executed.label',
          value: 'executed',
        },
        {
          label: 'entities:travel.attributes.executionStatus.selectOptions.cancelled.label',
          value: 'cancelled',
        },
      ],
      validationType: 'string',
      validations: [],
    },
    travelConsultant: {
      name: 'travelConsultant',
      label: 'entities:travel.attributes.travelConsultant.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return employeeEntity
      },
      defaultInput: true,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    label: {
      name: 'label',
      label: 'entities:travel.attributes.label.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return travelLabelEntity
      },
      defaultInput: true,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    title: {
      name: 'title',
      label: 'entities:travel.attributes.title.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    description: {
      name: 'description',
      label: 'entities:travel.attributes.description.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'wysiwyg',
      validationType: 'string',
      validations: [],
    },
    introVideoUrl: {
      name: 'introVideoUrl',
      label: 'entities:travel.attributes.introVideoUrl.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    includedServices: {
      name: 'includedServices',
      label: 'entities:travel.attributes.includedServices.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'wysiwyg',
      validationType: 'string',
      validations: [],
    },
    excludedServices: {
      name: 'excludedServices',
      label: 'entities:travel.attributes.excludedServices.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'wysiwyg',
      validationType: 'string',
      validations: [],
    },
    pricingTable: {
      name: 'pricingTable',
      label: 'entities:travel.attributes.pricingTable.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'wysiwyg',
      validationType: 'string',
      validations: [],
    },
    priceCalculationInfo: {
      name: 'priceCalculationInfo',
      label: 'entities:travel.attributes.priceCalculationInfo.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'textarea',
      validationType: 'string',
      validations: [],
    },
    seoSettings: {
      name: 'seoSettings',
      label: 'entities:travel.attributes.seoSettings.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return elementSeoSettingsEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    tenantSettings: {
      name: 'tenantSettings',
      label: 'entities:travel.attributes.tenantSettings.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelObjectTenantSettingsEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    highlights: {
      name: 'highlights',
      label: 'entities:travel.attributes.highlights.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelHighlightEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    partners: {
      name: 'partners',
      label: 'entities:travel.attributes.partners.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelPartnerEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    travelGuides: {
      name: 'travelGuides',
      label: 'entities:travel.attributes.travelGuides.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelGuideEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    teams: {
      name: 'teams',
      label: 'entities:travel.attributes.teams.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelTeamEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    accommodationAssignments: {
      name: 'accommodationAssignments',
      label: 'entities:travel.attributes.accommodationAssignments.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelAccommodationAssignmentEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    accommodations: {
      name: 'accommodations',
      label: 'entities:travel.attributes.accommodations.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return accommodationEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    destinations: {
      name: 'destinations',
      label: 'entities:travel.attributes.destinations.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelDestinationEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    testimonials: {
      name: 'testimonials',
      label: 'entities:travel.attributes.testimonials.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelTestimonialEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    mainImage: {
      name: 'mainImage',
      label: 'entities:travel.attributes.mainImage.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return mediaFileEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    mainVideo: {
      name: 'mainVideo',
      label: 'entities:travel.attributes.mainVideo.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return mediaFileEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    gallery: {
      name: 'gallery',
      label: 'entities:travel.attributes.gallery.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return imageGalleryEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    travelPdf: {
      name: 'travelPdf',
      label: 'entities:travel.attributes.travelPdf.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return mediaFileEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    travelExtensions: {
      name: 'travelExtensions',
      label: 'entities:travel.attributes.travelExtensions.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelExtensionEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    programType: {
      name: 'programType',
      label: 'entities:travel.attributes.programType.label',
      type: 'value',
      dataType: 'string',
      defaultValue: 'ITINERARY',
      defaultInput: false,
      inputType: 'select',
      selectOptions: [
        {
          label: 'entities:travel.attributes.programType.selectOptions.ITINERARY.label',
          value: 'ITINERARY',
        },
        {
          label: 'entities:travel.attributes.programType.selectOptions.TEXT.label',
          value: 'TEXT',
        },
      ],
      validationType: 'string',
      validations: [],
    },
    itinerary: {
      name: 'itinerary',
      label: 'entities:travel.attributes.itinerary.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return itineraryEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    programText: {
      name: 'programText',
      label: 'entities:travel.attributes.programText.label',
      type: 'value',
      dataType: 'string',
      defaultInput: false,
      inputType: 'wysiwyg',
      validationType: 'string',
      validations: [],
    },
    pricing: {
      name: 'pricing',
      label: 'entities:travel.attributes.pricing.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return travelPricingEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    minPrice: {
      name: 'minPrice',
      label: 'entities:travel.attributes.minPrice.label',
      type: 'value',
      dataType: 'number',
      defaultInput: false,
      inputType: 'hidden',
      validationType: 'string',
      validations: [],
    },
    minPriceManualOverride: {
      name: 'minPriceManualOverride',
      label: 'entities:travel.attributes.minPriceManualOverride.label',
      type: 'value',
      dataType: 'boolean',
      defaultInput: false,
      inputType: 'switch',
      validationType: 'string',
      validations: [],
    },
    currency: {
      name: 'currency',
      label: 'entities:travel.attributes.currency.label',
      type: 'value',
      dataType: 'string',
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    faqs: {
      name: 'faqs',
      label: 'entities:travel.attributes.faqs.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return travelFaqEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
  },
}

export default travelModel
